import React from "react";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import "./NegotiationBalance.css";

export type BalanceChipProps = {
  /** Confirmed balance minus every open reservation of the company; null while the first read is out. */
  availableCents: number | null;
  /** Top-ups in transit. Shown apart and never counted. */
  pendingTopupCents: number;
  /** A returned Pix restricted the balance: bids are refused until it is regularized. */
  restricted?: boolean;
  /** Absent hides the shortcut (no permission to recharge, or top-ups are off). */
  onRecharge?: () => void;
};

/**
 * The Saldo de Negociações in the Átrio header, beside the Total efetivo: what
 * is still available for new bids, and the recharge shortcut right after it.
 * What open bids reserve stays on the payments page, because on the board it
 * repeats the Total efetivo. It never adds the amount in confirmation to what
 * is available, and it never mixes in the Pix Automático weekly limit.
 * @story ancestor #negotiationHomeContainer
 * @story css ../../routes/pages/negotiate.css
 * @story args { availableCents: 85000, pendingTopupCents: 0 }
 * @story state InConfirmation { pendingTopupCents: 5000 }
 * @story state Restricted { availableCents: 0, restricted: true }
 * @story state WithoutRecharge { onRecharge: undefined }
 * @story state Loading { availableCents: null }
 */
export function BalanceChip({
  availableCents,
  pendingTopupCents,
  restricted = false,
  onRecharge,
}: BalanceChipProps) {
  const loading = availableCents === null;
  return (
    <div
      className="negotiationBalanceChip"
      role="group"
      aria-label="Saldo de Negociações"
      aria-busy={loading || undefined}
      data-testid="negotiation-balance-chip"
    >
      <div className="negotiationBalanceChipLine">
        <span
          className="negotiationBalanceChipSummary"
          data-testid="negotiation-balance-chip-summary"
        >
          Saldo disponível <strong>{loading ? "…" : formatBRL(availableCents)}</strong>
        </span>
        {onRecharge && !loading && (
          <button type="button" className="negotiationBalanceRechargeButton" onClick={onRecharge}>
            Recarregar
          </button>
        )}
      </div>
      {pendingTopupCents > 0 && (
        <small
          className="negotiationBalanceChipSecondary"
          data-testid="negotiation-balance-pending"
        >
          Em confirmação {formatBRL(pendingTopupCents)}
        </small>
      )}
      {restricted && (
        <small className="negotiationBalanceChipRestricted" role="status">
          Saldo bloqueado por devolução de Pix; recarregue para regularizar.
        </small>
      )}
    </div>
  );
}
